import React, { createContext, useContext, useEffect, useState } from 'react';

interface SettingsContextType {
  // Header and Navigation
  isStickyHeaderEnabled: boolean;
  toggleStickyHeader: () => void;
  
  // Notifications
  enableNotifications: boolean;
  toggleNotifications: () => void;
  enableSoundAlerts: boolean;
  toggleSoundAlerts: () => void;
  enableEmailNotifications: boolean;
  toggleEmailNotifications: () => void;
  
  // Data and Privacy
  autoSaveEnabled: boolean;
  toggleAutoSave: () => void;
  dataRetentionDays: number;
  setDataRetentionDays: (days: number) => void;
  enableAnalytics: boolean;
  toggleAnalytics: () => void;
  
  // UI and Display
  enableAnimations: boolean;
  toggleAnimations: () => void;
  compactMode: boolean;
  toggleCompactMode: () => void;
  showTooltips: boolean;
  toggleTooltips: () => void;
  enableHoverEffects: boolean;
  toggleHoverEffects: () => void;
  
  // Performance
  enableCaching: boolean;
  toggleCaching: () => void;
  cacheExpiryHours: number;
  setCacheExpiryHours: (hours: number) => void;
  enableLazyLoading: boolean;
  toggleLazyLoading: () => void;
  
  // Cricket Specific
  defaultOvers: number;
  setDefaultOvers: (overs: number) => void;
  enableBallByBallUpdates: boolean;
  toggleBallByBallUpdates: () => void;
  showDetailedStats: boolean;
  toggleDetailedStats: () => void;
  enableLiveScoreboard: boolean;
  toggleLiveScoreboard: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Header and Navigation
  const [isStickyHeaderEnabled, setIsStickyHeaderEnabled] = useState(() => {
    const storedValue = localStorage.getItem('stickyHeaderEnabled');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  // Notifications
  const [enableNotifications, setEnableNotifications] = useState(() => {
    const storedValue = localStorage.getItem('enableNotifications');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [enableSoundAlerts, setEnableSoundAlerts] = useState(() => {
    const storedValue = localStorage.getItem('enableSoundAlerts');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [enableEmailNotifications, setEnableEmailNotifications] = useState(() => {
    const storedValue = localStorage.getItem('enableEmailNotifications');
    return storedValue ? JSON.parse(storedValue) : false;
  });

  // Data and Privacy
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(() => {
    const storedValue = localStorage.getItem('autoSaveEnabled');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [dataRetentionDays, setDataRetentionDays] = useState(() => {
    const storedValue = localStorage.getItem('dataRetentionDays');
    return storedValue ? parseInt(storedValue) : 30;
  });

  const [enableAnalytics, setEnableAnalytics] = useState(() => {
    const storedValue = localStorage.getItem('enableAnalytics');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  // UI and Display
  const [enableAnimations, setEnableAnimations] = useState(() => {
    const storedValue = localStorage.getItem('enableAnimations');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [compactMode, setCompactMode] = useState(() => {
    const storedValue = localStorage.getItem('compactMode');
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [showTooltips, setShowTooltips] = useState(() => {
    const storedValue = localStorage.getItem('showTooltips');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [enableHoverEffects, setEnableHoverEffects] = useState(() => {
    const storedValue = localStorage.getItem('enableHoverEffects');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  // Performance
  const [enableCaching, setEnableCaching] = useState(() => {
    const storedValue = localStorage.getItem('enableCaching');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [cacheExpiryHours, setCacheExpiryHours] = useState(() => {
    const storedValue = localStorage.getItem('cacheExpiryHours');
    return storedValue ? parseInt(storedValue) : 24;
  });

  const [enableLazyLoading, setEnableLazyLoading] = useState(() => {
    const storedValue = localStorage.getItem('enableLazyLoading');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  // Cricket Specific
  const [defaultOvers, setDefaultOvers] = useState(() => {
    const storedValue = localStorage.getItem('defaultOvers');
    return storedValue ? parseInt(storedValue) : 20;
  });

  const [enableBallByBallUpdates, setEnableBallByBallUpdates] = useState(() => {
    const storedValue = localStorage.getItem('enableBallByBallUpdates');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [showDetailedStats, setShowDetailedStats] = useState(() => {
    const storedValue = localStorage.getItem('showDetailedStats');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  const [enableLiveScoreboard, setEnableLiveScoreboard] = useState(() => {
    const storedValue = localStorage.getItem('enableLiveScoreboard');
    return storedValue ? JSON.parse(storedValue) : true;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('stickyHeaderEnabled', JSON.stringify(isStickyHeaderEnabled));
  }, [isStickyHeaderEnabled]);

  useEffect(() => {
    localStorage.setItem('enableNotifications', JSON.stringify(enableNotifications));
  }, [enableNotifications]);

  useEffect(() => {
    localStorage.setItem('enableSoundAlerts', JSON.stringify(enableSoundAlerts));
  }, [enableSoundAlerts]);

  useEffect(() => {
    localStorage.setItem('enableEmailNotifications', JSON.stringify(enableEmailNotifications));
  }, [enableEmailNotifications]);

  useEffect(() => {
    localStorage.setItem('autoSaveEnabled', JSON.stringify(autoSaveEnabled));
  }, [autoSaveEnabled]);

  useEffect(() => {
    localStorage.setItem('dataRetentionDays', dataRetentionDays.toString());
  }, [dataRetentionDays]);

  useEffect(() => {
    localStorage.setItem('enableAnalytics', JSON.stringify(enableAnalytics));
  }, [enableAnalytics]);

  useEffect(() => {
    localStorage.setItem('enableAnimations', JSON.stringify(enableAnimations));
  }, [enableAnimations]);

  useEffect(() => {
    localStorage.setItem('compactMode', JSON.stringify(compactMode));
  }, [compactMode]);

  useEffect(() => {
    localStorage.setItem('showTooltips', JSON.stringify(showTooltips));
  }, [showTooltips]);

  useEffect(() => {
    localStorage.setItem('enableHoverEffects', JSON.stringify(enableHoverEffects));
  }, [enableHoverEffects]);

  useEffect(() => {
    localStorage.setItem('enableCaching', JSON.stringify(enableCaching));
  }, [enableCaching]);

  useEffect(() => {
    localStorage.setItem('cacheExpiryHours', cacheExpiryHours.toString());
  }, [cacheExpiryHours]);

  useEffect(() => {
    localStorage.setItem('enableLazyLoading', JSON.stringify(enableLazyLoading));
  }, [enableLazyLoading]);

  useEffect(() => {
    localStorage.setItem('defaultOvers', defaultOvers.toString());
  }, [defaultOvers]);

  useEffect(() => {
    localStorage.setItem('enableBallByBallUpdates', JSON.stringify(enableBallByBallUpdates));
  }, [enableBallByBallUpdates]);

  useEffect(() => {
    localStorage.setItem('showDetailedStats', JSON.stringify(showDetailedStats));
  }, [showDetailedStats]);

  useEffect(() => {
    localStorage.setItem('enableLiveScoreboard', JSON.stringify(enableLiveScoreboard));
  }, [enableLiveScoreboard]);

  // Toggle functions
  const toggleStickyHeader = () => setIsStickyHeaderEnabled(prev => !prev);
  const toggleNotifications = () => setEnableNotifications(prev => !prev);
  const toggleSoundAlerts = () => setEnableSoundAlerts(prev => !prev);
  const toggleEmailNotifications = () => setEnableEmailNotifications(prev => !prev);
  const toggleAutoSave = () => setAutoSaveEnabled(prev => !prev);
  const toggleAnalytics = () => setEnableAnalytics(prev => !prev);
  const toggleAnimations = () => setEnableAnimations(prev => !prev);
  const toggleCompactMode = () => setCompactMode(prev => !prev);
  const toggleTooltips = () => setShowTooltips(prev => !prev);
  const toggleHoverEffects = () => setEnableHoverEffects(prev => !prev);
  const toggleCaching = () => setEnableCaching(prev => !prev);
  const toggleLazyLoading = () => setEnableLazyLoading(prev => !prev);
  const toggleBallByBallUpdates = () => setEnableBallByBallUpdates(prev => !prev);
  const toggleDetailedStats = () => setShowDetailedStats(prev => !prev);
  const toggleLiveScoreboard = () => setEnableLiveScoreboard(prev => !prev);

  return (
    <SettingsContext.Provider value={{
      isStickyHeaderEnabled,
      toggleStickyHeader,
      enableNotifications,
      toggleNotifications,
      enableSoundAlerts,
      toggleSoundAlerts,
      enableEmailNotifications,
      toggleEmailNotifications,
      autoSaveEnabled,
      toggleAutoSave,
      dataRetentionDays,
      setDataRetentionDays,
      enableAnalytics,
      toggleAnalytics,
      enableAnimations,
      toggleAnimations,
      compactMode,
      toggleCompactMode,
      showTooltips,
      toggleTooltips,
      enableHoverEffects,
      toggleHoverEffects,
      enableCaching,
      toggleCaching,
      cacheExpiryHours,
      setCacheExpiryHours,
      enableLazyLoading,
      toggleLazyLoading,
      defaultOvers,
      setDefaultOvers,
      enableBallByBallUpdates,
      toggleBallByBallUpdates,
      showDetailedStats,
      toggleDetailedStats,
      enableLiveScoreboard,
      toggleLiveScoreboard,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}; 